import React from 'react';
import clsx from 'clsx';
import * as PrimitiveToggleGroup from "@radix-ui/react-toggle-group";
import { useField, useFormikContext } from "formik";
import { ScopedRequestType } from "lib/generated/graphql";

const toggleClasses = 'flex-1 flex flex-col text-center items-center border-[1.5px] bg-white pt-6 pb-8 px-4 rounded-xl transition-all outline outline-transparent';

const ScopeTypeSelect = () => {
  const { setFieldValue } = useFormikContext();
  const [field] = useField('type');

  const handleValueChange = (value) => {
    setFieldValue('type', value || null);
  };

  return (
    <div className="w-full">
      <div className="mb-4">
        <h2 className="text-2xl">How should we start?</h2>
        <p>If you’re not sure, you can skip this part. We’ll sort it later.</p>
      </div>
      <PrimitiveToggleGroup.Root
          aria-label="Scoped Engagement Type"
          className="flex space-x-2 w-full"
          type="single"
          defaultValue={null}
          value={field.value}
          onValueChange={handleValueChange}
      >
        <PrimitiveToggleGroup.Item
          value={ScopedRequestType.Advisor}
          className={clsx(toggleClasses, "border-[#EBB76A] hover:bg-[#FDF7F0] data-[state=on]:outline-[#EBB76A] data-[state=on]:bg-[#FDF7F0]")}
          aria-label="Start small with an Advisor"
        >
          <svg className="h-12 w-12 fill-current text-[#EBB76A]" xmlns="http://www.w3.org/2000/svg">
            <path d="M0.835938 0C14.0911 0 24.8359 10.7448 24.8359 24C11.5807 24 0.835938 13.2552 0.835938 0Z" fill="#EBB76A"/>
            <path d="M48.8359 0C35.5807 0 24.8359 10.7448 24.8359 24C38.0911 24 48.8359 13.2552 48.8359 0Z" fill="#EBB76A"/>
            <path d="M0.835938 48C14.0911 48 24.8359 37.2552 24.8359 24C11.5807 24 0.835938 34.7448 0.835938 48Z" fill="#EBB76A"/>
            <path d="M48.8359 48C35.5807 48 24.8359 37.2552 24.8359 24C38.0911 24 48.8359 34.7448 48.8359 48Z" fill="#EBB76A"/>
          </svg>
          <h3 className="mt-3 font-bold text-[#6B431A]">Start small with an Advisor</h3>
          <p>Dedicated 2-5 hours per week (20hrs total per month)</p>
          <h4 className="mt-3 text-[#6B431A]">Starting at $2500/mo</h4>
        </PrimitiveToggleGroup.Item>
        <PrimitiveToggleGroup.Item
          value={ScopedRequestType.Fractional}
          className={clsx(toggleClasses, "border-[#364442] hover:bg-[#EAECEC] data-[state=on]:outline-[#364442] data-[state=on]:bg-[#EAECEC]")}
          aria-label="Start with a Fractional Teammate"
        >
          <svg className="h-12 w-12 fill-current text-[#364442]" xmlns="http://www.w3.org/2000/svg">
            <path d="M16.5008 32.0016C25.3378 32.0016 32.5016 24.8378 32.5016 16.0008C32.5016 7.1638 25.3378 0 16.5008 0C7.6638 0 0.5 7.1638 0.5 16.0008C0.5 24.8378 7.6638 32.0016 16.5008 32.0016Z" fill="#ABBBB9"/>
            <path d="M32.5 0V31.9992C41.3368 31.9992 48.5008 24.8352 48.5008 15.9984C48.5008 7.1616 41.3368 0 32.5 0Z" fill="#ABBBB9"/>
            <path d="M32.4992 32H0.5C0.5 40.8368 7.664 48.0008 16.5008 48.0008C25.3376 48.0008 32.5016 40.8368 32.5016 32H32.4992Z" fill="#ABBBB9"/>
            <path d="M32.5 48.0008C41.3368 48.0008 48.5008 40.8368 48.5008 32H32.5V48.0008Z" fill="#ABBBB9"/>
          </svg>
          <h3 className="mt-3 font-bold text-[#364442]">Start with a Fractional Teammate</h3>
          <p>Dedicated 1+ days per week</p>
          <h4 className="mt-3 text-[#364442]">Starting at $5,000/mo</h4>
        </PrimitiveToggleGroup.Item>
        <PrimitiveToggleGroup.Item
          value={ScopedRequestType.Team}
          className={clsx(toggleClasses, "border-[#493C63] hover:bg-[#ECEBEF] data-[state=on]:outline-[#493C63] data-[state=on]:bg-[#ECEBEF]")}
          aria-label="Get a Team"
        >
          <svg className="h-12 w-12 fill-current text-[#493C63]" xmlns="http://www.w3.org/2000/svg">
            <path d="M24.1719 12C24.1719 5.3736 18.7983 0 12.1719 0C5.54547 0 0.171875 5.3736 0.171875 12C0.171875 18.6264 5.54547 24 12.1719 24C12.1719 17.3736 17.5455 12 24.1719 12Z" fill="#BEB4D2"/>
            <path d="M36.1719 24C42.7983 24 48.1719 18.6264 48.1719 12C48.1719 5.3736 42.7983 0 36.1719 0C29.5455 0 24.1719 5.3736 24.1719 12C30.7983 12 36.1719 17.3736 36.1719 24Z" fill="#BEB4D2"/>
            <path d="M12.1719 24C5.54547 24 0.171875 29.3736 0.171875 36C0.171875 42.6264 5.54547 48 12.1719 48C18.7983 48 24.1719 42.6264 24.1719 36C17.5455 36 12.1719 30.6264 12.1719 24Z" fill="#BEB4D2"/>
            <path d="M36.1719 24C36.1719 30.6264 30.7983 36 24.1719 36C24.1719 42.6264 29.5455 48 36.1719 48C42.7983 48 48.1719 42.6264 48.1719 36C48.1719 29.3736 42.7983 24 36.1719 24Z" fill="#BEB4D2"/>
            <path d="M36.1719 24C29.5455 24 24.1719 18.6264 24.1719 12C24.1719 18.6264 18.7983 24 12.1719 24C18.7983 24 24.1719 29.3736 24.1719 36C24.1719 29.3736 29.5455 24 36.1719 24Z" fill="#BEB4D2"/>
          </svg>
          <h3 className="mt-3 font-bold text-[#493C63]">Get a Team</h3>
          <p>2+ Fractional Builders</p>
          <h4 className="mt-3 text-[#493C63]">Starting at $10,000 and up</h4>
        </PrimitiveToggleGroup.Item>
      </PrimitiveToggleGroup.Root>
    </div>
  );
}

export default ScopeTypeSelect;
